import { LocationId } from '../location';
import { HasId, Auditable, ObjectOrVoid, PathBuilder, SeekingPage, HttpMethod, ApiRoute } from '../common';
import { Upload, UploadId } from '../upload';
import { Platform } from '../platform';
import { UserId, AssignedUser } from '../user/User';
import { PublishedArtifactId } from '../publishedArtifact';

export type ScheduledPostType = 'image' | 'video';
export type ScheduledPostPlatform = 'instagram';
export type ScheduledPostStatus = 'dismissed' | 'posted' | 'ready';

export interface ScheduledPost extends HasId, Auditable {
    key: string;
    keyExpiration: Date;
    scheduledTime: Date;
    platformId: number;
    platform?: Platform;
    type: ScheduledPostType;
    caption: string | null;
    locationId: LocationId;
    locationDisplayName: string;
    uploadId: UploadId;
    upload?: Upload;
    contentUrl: string;
    messageId?: number;
    contactNumber: string | null;
    status: ScheduledPostStatus;
    assignedUserId: UserId;
    assignedUser?: AssignedUser;
    platformAccountName: string | null;
    isLegacyPost: boolean;
    publishedArtifactId: PublishedArtifactId | null;
}

export type ScheduledPostDTO = {
    id: number;
    scheduledTime: string;
    platform?: Platform;
    caption: string | null;
    locationDisplayName: string;
    upload?: Upload;
    contentUrl: string;
    status: ScheduledPostStatus;
    platformAccountName: string | null;
};

export const DEFAULT_PAGING_SIZE = 8;

const SCHEDULED_POSTS_API_ROOT = <T extends ObjectOrVoid>(path: PathBuilder<T>) => path.literal('scheduledPosts');

const SCHEDULED_POSTS_PLATFORM_API_ROOT = <T extends { platform: 'instagram' }>(path: PathBuilder<T>) =>
    SCHEDULED_POSTS_API_ROOT(path).param('platform');

export const ScheduledPostsApi = {
    getPostByKey: new ApiRoute<ScheduledPostDTO, { key: string }, void, void>(HttpMethod.Get, path =>
        SCHEDULED_POSTS_API_ROOT(path).param('key'),
    ),
    getReadyPostsByPlatform: new ApiRoute<ScheduledPost[], { platform: 'instagram' }, void, SeekingPage>(
        HttpMethod.Get,
        path => SCHEDULED_POSTS_PLATFORM_API_ROOT(path).literal('ready'),
    ),
    getUpcomingPostsByPlatform: new ApiRoute<ScheduledPost[], { platform: 'instagram' }, void, SeekingPage>(
        HttpMethod.Get,
        path => SCHEDULED_POSTS_PLATFORM_API_ROOT(path).literal('upcoming'),
    ),
    updateScheduledPostStatus: new ApiRoute<
        ScheduledPostDTO,
        { scheduledPostId: number },
        { status: ScheduledPostStatus },
        void
    >(HttpMethod.Patch, path => SCHEDULED_POSTS_API_ROOT(path).param('scheduledPostId').literal('status')),
};

export interface ISchedulePostNotificationWorkflowInput {
    scheduledTime: string;
    scheduledPostId: number;
}
